import mappingData from './specialityPageMapping.json';

export interface SpecialityPageMappingEntry {
  pageName: string;
  path: string;
  pageType: string;
  primarySpeciality: string;
  relevantSpecialities: string[];
}

export const SPECIALITY_PAGE_MAPPING =
  mappingData as SpecialityPageMappingEntry[];

/**
 * Normalises a speciality name or slug for lookup:
 * lowercase, "&" -> "and", strip every non-alphanumeric character.
 * e.g. "Trauma & Orthopaedics (Lower Limb)" -> "traumaandorthopaedicslowerlimb"
 */
export const normaliseSpecialityName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');

// Lazy-built lookup: normalised speciality name -> { path, tier }.
// Lower tier wins. Tiers:
//   1 — the row's page IS the speciality page (pageName matches)
//   2 — primarySpeciality matches and the page is a Specialty page
//   3 — primarySpeciality matches (service/treatment/diagnostic pages)
//   4 — speciality only listed under "Relevant Specialties"
let lookup: Map<string, { path: string; tier: number }> | null = null;

const buildLookup = () => {
  if (lookup) return;
  lookup = new Map();
  const setIfBetter = (key: string, path: string, tier: number) => {
    if (!key) return;
    const existing = lookup!.get(key);
    if (!existing || tier < existing.tier) {
      lookup!.set(key, { path, tier });
    }
  };
  for (const entry of SPECIALITY_PAGE_MAPPING) {
    const isSpecialtyPage = /specialty/i.test(entry.pageType);
    setIfBetter(normaliseSpecialityName(entry.pageName), entry.path, 1);
    setIfBetter(
      normaliseSpecialityName(entry.primarySpeciality),
      entry.path,
      isSpecialtyPage ? 2 : 3,
    );
    for (const name of entry.relevantSpecialities) {
      setIfBetter(normaliseSpecialityName(name), entry.path, 4);
    }
  }
};

/**
 * Resolves the marketing page path for a speciality name or slug using the
 * "Speciality mapping for CCSD codes" data as the primary source.
 * Returns null when nothing in the mapping matches.
 */
export const getSpecialityPagePath = (nameOrSlug: string): string | null => {
  buildLookup();
  const key = normaliseSpecialityName(nameOrSlug);
  if (!key) return null;
  return lookup?.get(key)?.path ?? null;
};
